import { h, Component } from 'preact';
import { track } from 'utils/tracking';
import { joinClasses, sortObjectByOrder } from 'utils/utils';
import { showTrailerModal } from 'utils/trailer';
import { fullPage, getData } from 'utils/dynamicData';
import { shouldShowNavigationLink } from 'utils/navigationHelpers';
import { addMovieStatus } from 'utils/movie';

import { Logo } from 'components/logo/logo';
import PageLink from 'components/pageLink/pageLink';

import s from 'components/header/header.sss';

export default class Header extends Component {
	constructor(props) {
		super(props);

		this.state = {
			links: [],
			open: false,
		};

		this.toggleOpen = this.toggleOpen.bind(this);
		this.onAnyClick = this.onAnyClick.bind(this);
		this.renderLink = this.renderLink.bind(this);
	}

	componentDidMount() {
		this.resizeObserver = new ResizeObserver(this.onResize);
		this.resizeObserver.observe(this.base);

		this.updateLinks();
	}

	componentWillUnmount() {
		this.resizeObserver?.disconnect();
		this.resizeObserver = null;
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.store?.activePageProps?.slug !== this.props.store?.activePageProps?.slug) {
			this.updateLinks(nextProps);
		}
	}

	showTrailer(movie) {
		const trailers = movie?.trailers;
		showTrailerModal(trailers);
	}

	async updateLinks(props = this.props) {
		this.setState({ links: [] });
		const slug = props.store?.activePageProps?.slug;
		if (!slug) return;
		try {
			let movie = await getData(slug, 'movie');
			if (!movie) throw new Error('Movie not found');

			// Add movie status (checks both page active AND dates)
			movie = await addMovieStatus(movie);

			// Get navigation links from CMS (or use defaults if not present) and sort
			const links = sortObjectByOrder(movie?.header?.links || this.getDefaultLinks(movie))
				.filter(link => shouldShowNavigationLink(link, movie)) // Filter links based on conditional logic and hidden status
				.map(link => this.convertLinkToComponent(link, movie)).filter(Boolean); // Convert to component format

			this.setState({ links });
		} catch (err) {}
	}

	getDefaultLinks(movie) {
		return {
			home: { id: 'home', name: movie?.title || 'Home', order: 0, permanent: true },
			videos: { id: 'videos', name: 'Videos', order: 1, default: true },
			showtimes: { id: 'showtimes', name: 'Tickets', order: 2, default: true },
			homeEnt: { id: 'homeEnt', name: 'Watch at Home', order: 3, default: true },
		};
	}

	convertLinkToComponent(link, movie) {
		// Handle default/permanent navigation items
		if (link.default || link.permanent) {
			switch (link.id) {
				case 'home':
					return { label: link.name, page: 'movie', props: { movie } };
				case 'videos':
					return { label: link.name, action: () => this.showTrailer(movie) };
				case 'showtimes':
					return { label: link.name, page: 'showtimes', props: { movie } };
				case 'homeEnt':
					return { label: link.name, page: 'homeEnt', props: { movie } };
				default:
					return null;
			}
		}

		// Handle custom navigation items (external links)
		if (link.destination) {
			return {
				label: link.name,
				href: link.destination,
				external: true
			};
		}

		return null;
	}

	toggleOpen() {
		this.setState({ open: !this.state.open });
	}

	onResize(e) {
		const height = e.at(-1)?.borderBoxSize?.[0]?.blockSize;
		if (height != undefined) {
			document.documentElement.style.setProperty('--header-height', `${height}px`);
		}
	}

	onLogoClick() {
		track('header', 'header-nav-click', 'Logo');
	}

	onAnyClick(e) {
		if (this.state.open && this.base.contains(e.target.closest(`.${s.drawer} :is(button, a)`))) {
			this.setState({ open: false });
		}
		track('header', 'header-nav-click', e.textContent);
	}

	renderLink(link) {
		if (!link) return;
		if (link.page) {
			// Note: should determining active pages be the task of PageLink (based on url)? e.g. activeClass={s.active}
			const query = fullPage ? { fullpage: '' } : undefined;
			return (
				<PageLink class={link.page === this.props.store?.activePage && s.active} to={link.page} query={query} {...link.props}>
					{link.label}
				</PageLink>
			);
		}
		if (link.action) {
			return (
				<button onClick={link.action} {...link.props}>{link.label}</button>
			);
		}
		if (link.external && link.href) {
			return (
				<a href={link.href} target="_blank" rel="noopener noreferrer" {...link.props}>
					{link.label}
				</a>
			);
		}
		if (link.label) {
			return <span {...link.props}>{link.label}</span>;
		}
	}

	render(props, state) {
		const query = fullPage ? { fullpage: '' } : undefined;
		return (
			<header class={joinClasses(s.wrapper, state.open && s.open, state.links.length && s.hasLinks)} onClickCapture={this.onAnyClick}>
				<h1 class={s.logo}>
					<PageLink to="landing" query={query} onClick={this.onLogoClick}>
						<Logo width="112" height="40" />
					</PageLink>
				</h1>
				{state.links.length > 0 && (
					<div class={s.links}>
						<button class={s.burger} onClick={this.toggleOpen} />
						<div class={s.drawer}>{state.links.map(this.renderLink)}</div>
					</div>
				)}
			</header>
		);
	}
}
